import React from 'react';

// Define the props for our Button component
interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    text: string;
    bgColor?: string;
    hoverColor?: string;
    rounded?: string;
    isActive?: boolean;
}

/**
 * A reusable button component with customizable text and colors.
 * It forwards all standard button attributes like onClick, disabled, etc.
 *
 * @param {string} text - The text to display inside the button.
 * @param {string} [bgColor='bg-[var(--primary-color)]'] - The background color class (Tailwind).
 * @param {string} [hoverColor='hover:bg-[var(--hover-color)]'] - The hover background color class (Tailwind).
 * @param {string} [rounded='rounded-md'] - The border radius class (Tailwind).
 * @param className
 * @param isActive
 * @param {Object} props - Other standard HTML button props (e.g., onClick, type, className, disabled).
 */
const CustomButton: React.FC<ButtonProps> = ({
                                           text,
                                           bgColor = 'bg-[var(--primary-color)]',
                                           hoverColor = 'hover:bg-[var(--hover-color)]',
                                           rounded = 'rounded-md',
                                           isActive,
                                           className,
                                           ...props
                                       }) => {
    // Combine default styles with any custom classes passed in
    const buttonClasses = [
        'text-white',
        'py-3',
        'px-5',
        'transition-colors',
        'duration-300',
        'ease-in-out',
        'disabled:opacity-50',
        'disabled:cursor-not-allowed',
        isActive ? 'bg-[var(--hover-color)]' : bgColor,
        hoverColor,
        rounded,
        className, // Allow overriding or adding classes
    ].filter(Boolean).join(' '); // filter(Boolean) removes any falsy values (like null or undefined className)

    return (
        <button className={buttonClasses} {...props}>
            {text}
        </button>
    );
};

export default CustomButton;